import type { CompetitionCustomers } from '@/api/competitionDetail'
import { EmptyState, formatCurrency, formatInteger, formatPercent, type FormatOptions } from '@/design'

import { Group, Tile, TileGroup } from '../pieces'

/**
 * Who bought: new and returning customers (the list's new-customer rule, per
 * competition) and guest checkouts, the period's revenue by whose order it was, and the
 * buyers by the channel of their own first touch. Customers are counted, never named.
 */
export function CustomersTab({ customers, money }: { customers: CompetitionCustomers; money: FormatOptions }) {
  const currency = (value: number) => formatCurrency(value, money)
  const { firstTouchChannels: channels } = customers
  const buyers = channels.reduce((sum, channel) => sum + channel.customers, 0)
  return (
    <div className="cd-stack">
      <TileGroup title="Buyers in this period">
        <Tile label="New customers" value={formatInteger(customers.newCustomers)} note="Their first order with you was in this period" />
        <Tile label="Returning customers" value={formatInteger(customers.returningCustomers)} note="Had ordered with you before" />
        <Tile label="Guest orders" value={formatInteger(customers.guestOrders)} note="Orders, not people: no customer account" />
      </TileGroup>
      <TileGroup title="Revenue by buyer">
        <Tile label="New customers" value={currency(customers.revenue.new)} />
        <Tile label="Returning customers" value={currency(customers.revenue.returning)} />
        <Tile label="Guests" value={currency(customers.revenue.guest)} />
      </TileGroup>
      <Group title="Buyers by first-touch channel">
        <p className="cd-note">Each buyer is placed by the channel of their own first recorded visit. Guests are not included.</p>
        {buyers === 0 ? (
          <EmptyState
            variant="no-results"
            compact
            title="No identified buyers in this period"
            description="No customer with an account bought a ticket for this competition in this period."
          />
        ) : (
          <div className="ci-table-wrap" role="region" aria-label="Buyers by first-touch channel" tabIndex={0}>
            <table className="ci-table">
              <caption className="ci-visually-hidden">Buyers by first-touch channel</caption>
              <thead>
                <tr>
                  <th scope="col">Channel</th>
                  <td className="cd-bar-col" aria-hidden="true" />
                  <th scope="col" data-align="end">
                    Buyers
                  </th>
                  <th scope="col" data-align="end">
                    Share
                  </th>
                </tr>
              </thead>
              <tbody>
                {channels.map((channel) => {
                  const share = channel.customers / buyers
                  return (
                    <tr key={channel.channel ?? 'unattributed'} data-channel={channel.channel ?? 'unattributed'}>
                      <th scope="row">{channel.label}</th>
                      <td className="cd-bar-col" aria-hidden="true">
                        <span className="ci-funnel__track">
                          <span className="ci-funnel__bar" style={{ width: `${Math.max(1.5, share * 100)}%` }} />
                        </span>
                      </td>
                      <td data-align="end">{formatInteger(channel.customers)}</td>
                      <td data-align="end">{formatPercent(share)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Group>
    </div>
  )
}
